'use client';

import React, { Suspense, lazy, useState } from 'react';
import PerspectiveDial from '@/components/PerspectiveDial';
import SEO from '@/components/SEO';
import { useLanguage } from '@/context/LanguageContext';

// Lazy load the 3D cube to improve initial load time
const PerspectiveCube = lazy(() => import('@/components/PerspectiveCube'));

const AboutPage: React.FC = () => {
    const [activeFace, setActiveFace] = useState<string>('products');
    const { t } = useLanguage();

    return (
        <main id="main-content" className="relative h-screen w-full overflow-hidden bg-neutral-950">
            {/* Force HMR Rev 2 */}
            <SEO
                title={t("seo.about.title")}
                description={t("seo.about.description")}
                keywords={t("seo.about.keywords").split(",").map((k) => k.trim())}
            />

            {/* 3D Cube - Full Screen Background Layer */}
            <div className="absolute inset-0 z-10 w-full h-full">
                <Suspense fallback={
                    <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <p className="text-gray-600 dark:text-gray-400">Loading 3D Experience...</p>
                        </div>
                    </div>
                }>
                    <PerspectiveCube
                        activeFace={activeFace}
                        onFaceChange={setActiveFace}
                    />
                </Suspense>
            </div>

            {/* Hero section with dial - Overlay Layer */}
            <div className="absolute top-0 left-0 w-full z-20 pointer-events-none pt-4 sm:pt-6">
                <div className="container mx-auto px-2 sm:px-4">
                    <div className="text-center pointer-events-auto">
                        <PerspectiveDial
                            activeFaceId={activeFace}
                            onPerspectiveChange={setActiveFace}
                        />
                    </div>
                </div>
            </div>

            {/* SEO Content (Hidden/Below Fold) */}
            <div className="sr-only">
                <h1>{t("seo.about.h1")}</h1>
                <p>
                    The Systemic Cube Model is a comprehensive framework for organizational development, designed by Kevin Rassner.
                    It visualizes the multi-dimensional nature of systemic change, integrating strategy, structure, and culture.
                    Each face of the cube represents a critical perspective: Products, Processes, People, and more.
                    By rotating the cube, you can explore how these dimensions interact and influence each other.
                    This interactive tool helps leaders and teams understand the complexity of their organization and identify leverage points for effective intervention.
                    Whether you are dealing with digital transformation, cultural change, or strategic realignment, the Systemic Cube offers a structured approach to navigate ambiguity.
                    Explore the different facets to gain insights into holistic organizational health and performance.
                    Systemic consulting goes beyond symptom treatment; it addresses root causes and fosters sustainable growth.
                    Use this model to diagnose issues, plan initiatives, and align your team towards a shared vision.
                    Kevin Rassner specializes in guiding organizations through these complex transitions, ensuring that every change is meaningful and lasting.
                </p>
            </div>
        </main>
    );
};

export default AboutPage;
